import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import DMPaywallModal from "@/components/messaging/DMPaywallModal";
import { markNotificationsRead, parseInquiryError, REASON_CODE_LABELS } from "@/lib/modulD";
import { ArrowLeft, Send, Clock, Flag } from "lucide-react";
import MessageReportDialog from "@/components/MessageReportDialog";
import RichTextEditor from "@/components/common/RichTextEditor";
import MessagingQuotaBanner from "@/components/messaging/MessagingQuotaBanner";

export default function DMThreadPage() {
  const { t } = useTranslation();
  const { threadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState({});
  // iter80.1: Post-Kontext (falls Thread aus Feed-Post gestartet)
  const [postContext, setPostContext] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false); // iter172f
  const [paywallCreatorId, setPaywallCreatorId] = useState(null);
  const [warning, setWarning] = useState(null);
  // Sub-Batch 4c.5: Report dialog state
  const [reportingMessage, setReportingMessage] = useState(null);
  const [reportedIds, setReportedIds] = useState(new Set());
  // iter138: Nachrichten-Kontingent gegenüber otherUser
  const [quota, setQuota] = useState(null);
  const scrollRef = useRef(null);
  const prefillApplied = useRef(false);

  const loadThread = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/social/threads/${threadId}`);
      setThread(data.thread);
      setMessages(data.messages || []);
      setOtherUser(data.other_user || {});
      setPostContext(data.post_context || null);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [threadId]);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    loadThread();
    // Mark notifications for this thread as read
    markNotificationsRead({ conversation_id: threadId }).catch(() => {});
    const interval = setInterval(loadThread, 10000);
    return () => clearInterval(interval);
  }, [user, navigate, threadId, loadThread]);

  // Apply prefill from URL query (once) — does NOT auto-send
  useEffect(() => {
    if (prefillApplied.current) return;
    const params = new URLSearchParams(location.search);
    const pre = params.get("prefill");
    if (pre) {
      setText(decodeURIComponent(pre));
      prefillApplied.current = true;
      // Clean up URL so prefill doesn't re-apply on reload
      const newSearch = new URLSearchParams(location.search);
      newSearch.delete("prefill");
      const newUrl = `${location.pathname}${newSearch.toString() ? "?" + newSearch.toString() : ""}`;
      window.history.replaceState({}, "", newUrl);
    }
  }, [location]);

  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendMsg = async () => {
    const plain = (text || "")
      .replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
      .replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
      .replace(/\n{3,}/g, "\n\n").trim();
    if (!plain || sending) return;
    setSending(true);
    setError(null);
    setWarning(null);
    try {
      const { data } = await api.post(`/messages/social/threads/${threadId}/reply`, { text: plain });
      setText("");
      if (data?.policy?.decision === "REDACTED" || data?.policy?.redaction_count > 0) {
        const c = data.policy.redaction_count;
        setWarning(
          `⚠ Hinweis: Deine Nachricht enthielt ${c || ""} ${c === 1 ? "Kontaktdatum" : "Kontaktdaten"} ` +
          `(z.B. Telefonnummer, E-Mail, Adresse). Diese wurden automatisch entfernt. ` +
          `Die Weitergabe persönlicher Daten ist auf stilltouched nicht zulässig.`
        );
      }
      loadThread();
    } catch (e) {
      // iter172e/f · FIRST_FREE_CONSUMED / DM_GATE_CLOSED → Nutzer auf Paywall / Optionen hinweisen
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && detail?.code === "REQUEST_PENDING") {
        setError(detail.message || "Deine Nachrichten-Anfrage wartet noch auf Freigabe.");
        return;
      }
      if (e?.response?.status === 403 && (detail?.code === "FIRST_FREE_CONSUMED" || detail?.code === "DM_GATE_CLOSED")) {
        setError(detail.message || "Deine kostenlose Erst-Nachricht wurde bereits gesendet.");
        // Direkt Paywall-Modal öffnen
        setPaywallCreatorId(detail.creator_id || null);
        setPaywallOpen(true);
        return;
      }
      const parsed = parseInquiryError(e);
      if (parsed.blocked) {
        setError(`${REASON_CODE_LABELS[parsed.reason_code] || "Blockiert"}: ${parsed.reason_text}`);
      } else {
        setError(parsed.message || "Nachricht konnte nicht gesendet werden.");
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;

  return (
    <div data-testid="dm-thread-page" className="flex flex-col h-[calc(100vh-7.5rem)]">
      {/* iter172e · Info-Banner nach erster kostenloser Nachricht */}
      {(() => {
        try {
          return sessionStorage.getItem(`first_free_used_${threadId}`) === "1";
        } catch { return false; /* ignore */ }
      })() && (
        <InfoCard variant="success" size="sm" testId="dm-first-free-banner" className="mx-3 mt-3 rounded-xl">
          <strong>{t("dm_thread.first_free_label")}</strong> Diese Unterhaltung wurde einmalig kostenlos freigeschaltet. Für weitere Nachrichten wird ein Abo oder Nachrichten-Paket benötigt.
        </InfoCard>
      )}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5] shrink-0">
        <button data-testid="dm-back" onClick={() => navigate(-1)}><ArrowLeft size={20} /></button>
        <div className="w-8 h-8 rounded-full bg-[#E5E5E5] flex items-center justify-center text-xs font-bold text-[#999] shrink-0">
          {(otherUser.display_name || "?")[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#0A0A0A] truncate">{otherUser.display_name || "User"}</p>
        </div>
      </div>

      {/* iter80.1: Post-Kontext-Banner — zeigt den Feed-Post, aus dem die Unterhaltung gestartet wurde */}
      {postContext && (
        <button
          data-testid="dm-post-context"
          onClick={() => {
            const h = postContext.author?.handle;
            navigate(h ? `/user/${h}?post=${postContext.post_id}` : "/");
          }}
          className="flex items-center gap-2 px-4 py-2 border-b border-[#E5E5E5] bg-[#FAFAFA] hover:bg-[#F0F0F0] text-left transition-colors shrink-0"
        >
          {postContext.media_url ? (
            <img src={postContext.media_url} alt="" className="w-10 h-10 object-cover shrink-0" />
          ) : (
            <div className="w-10 h-10 bg-[#E5E5E5] shrink-0 flex items-center justify-center">
              <span className="text-[9px] text-[#999]">POST</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-[#999]">
              Bezogen auf Beitrag von {postContext.author?.display_name || "Unknown"}
            </p>
            <p className="text-xs text-[#0A0A0A] truncate">
            {postContext.title || postContext.text || "(kein Text)"}
            </p>
          </div>
        </button>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((m) => {
          const mine = m.sender_id === user?.id;
          const canReport = !mine;
          const alreadyReported = reportedIds.has(m.message_id);
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
                <div className={`px-3 py-2 ${mine ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#0A0A0A]"}`}>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</p>
                  {m.redaction_count > 0 && (
                    <p className={`text-[10px] mt-1 italic ${mine ? "text-amber-300/80" : "text-amber-700"}`}>
                      ⚠ {m.redaction_count} Kontaktdaten automatisch entfernt
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className={`text-[9px] ${mine ? "text-[#CCC]" : "text-[#999]"}`}>
                    {new Date(m.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                  {canReport && (
                    <button
                      data-testid={`report-btn-${m.message_id}`}
                      onClick={() => setReportingMessage(m)}
                      disabled={alreadyReported}
                      className={`text-[10px] flex items-center gap-0.5 ${
                        alreadyReported ? "text-[#CCC] cursor-default" : "text-[#999] hover:text-red-600"
                      }`}
                      title={alreadyReported ? "Bereits gemeldet" : "Diese Nachricht melden"}
                    >
                      <Flag size={9} />
                      {alreadyReported ? "gemeldet" : "melden"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={scrollRef} />
      </div>

      <div className="shrink-0 border-t border-[#E5E5E5] px-4 py-3">
        {/* iter138: Nachrichten-Kontingent + Paket-Kauf + Tip-Fallback */}
        <MessagingQuotaBanner creator={otherUser} onQuotaChange={setQuota} />
        {error && (
          <InfoCard variant="danger" className="mb-2">
            <p className="text-[11px] text-red-700">{error}</p>
          </InfoCard>
        )}
        {warning && (
          <InfoCard variant="warning" className="mb-2">
            <p className="text-[11px] text-amber-900 leading-relaxed">{warning}</p>
            <button onClick={() => setWarning(null)} className="mt-1 text-[10px] text-amber-700 underline hover:text-amber-900">
              Verstanden
            </button>
          </InfoCard>
        )}
        <div className="flex gap-2 items-end">
          <div data-testid="dm-input" className="flex-1">
            <RichTextEditor
              value={text}
              onChange={(html) => setText(html)}
              placeholder={t("dm_thread.placeholder")}
              minHeight="60px"
              testId="dm-input-rt"
            />
          </div>
          <button data-testid="dm-send" onClick={sendMsg}
            disabled={sending || !(text || "").replace(/<[^>]+>/g, "").trim() || (quota && !quota.unlimited && Number(quota.total_remaining || 0) <= 0)}
            className="rounded-full h-10 w-10 bg-[#0A0A0A] text-white flex items-center justify-center disabled:opacity-30 shrink-0">
            {sending ? <Clock size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>

      {/* Message-Report-Dialog (Sub-Batch 4c.5) */}
      <MessageReportDialog
        open={!!reportingMessage}
        onClose={() => setReportingMessage(null)}
        messageKind="DM"
        messageId={reportingMessage?.message_id}
        messagePreview={reportingMessage?.text}
        onReported={() => {
          if (reportingMessage?.message_id) {
            setReportedIds((prev) => new Set([...prev, reportingMessage.message_id]));
          }
        }}
      />
      {/* iter172f · DM-Paywall-Modal bei FIRST_FREE_CONSUMED / DM_GATE_CLOSED */}
      <DMPaywallModal
        open={paywallOpen}
        creatorId={paywallCreatorId || (thread?.participant_a === user?.id ? thread?.participant_b : thread?.participant_a)}
        creatorName={thread?.other_user?.display_name || thread?.other_user?.handle}
        onClose={() => setPaywallOpen(false)}
      />
    </div>
  );
}
